import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import { media } from "../../server";
import { useStateContext } from "../../context/ContextProvider";

import {
  FiStar,
  FiClock,
  FiArrowRight,
} from "react-icons/fi";

import { FaFireAlt } from "react-icons/fa";

function MealsCard({
  meals,
  viewMode = "grid",
  compact = false,
}) {
  const { currentDir } = useStateContext();
  const { t } = useTranslation();

  const rating = 4.5;

  // =========================
  // Meal Data
  // =========================

  const title =
    currentDir === "ltr"
      ? meals?.titleEn
      : meals?.titleAr;

  const description =
    currentDir === "ltr"
      ? meals?.descriptionEn
      : meals?.descriptionAr;

  const categoryTitle =
    currentDir === "ltr"
      ? meals?.categoryId?.titleEn
      : meals?.categoryId?.titleAr;

  const preparationTime =
    meals?.preparationTime
      ? `${meals.preparationTime} ${t("minutes")}`
      : `15-20 ${t("minutes")}`;

  // =========================
  // Price
  // =========================

  const hasPrice =
    meals?.price !== undefined &&
    meals?.price !== null &&
    meals?.price !== "";

  const formattedPrice = hasPrice
    ? Number(meals.price).toLocaleString(
        currentDir === "rtl"
          ? "ar-EG"
          : "en-US"
      )
    : null;

  // =========================
  // Scroll Top
  // =========================

  const handleScrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // LIST VIEW
  // =========================================================

  if (viewMode === "list") {
    return (
      <motion.article
        whileHover={{ y: -4 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 24,
        }}
        className="
          group
          relative
          flex
          flex-col
          sm:flex-row
          overflow-hidden
          rounded-3xl
          border
          border-color-1/10
          bg-white
          dark:bg-back-darktertiary
          shadow-lg
          transition-all
          duration-500
          hover:border-color-1/30
          hover:shadow-2xl
        "
      >
        {/* ================= Image ================= */}

        <div
          className="
            relative
            w-full
            h-56
            sm:w-56
            md:w-72
            sm:h-auto
            shrink-0
            overflow-hidden
          "
        >
          <img
            src={`${media}${meals?.Image}`}
            alt={title}
            loading="lazy"
            className="
              w-full
              h-full
              object-cover
              transition-transform
              duration-700
              group-hover:scale-110
            "
          />

          {/* Image Overlay */}
          <div
            className="
              absolute
              inset-0
              bg-gradient-to-t
              from-black/40
              via-transparent
              to-transparent
            "
          />

          {/* Category */}
          <div
            className="
              absolute
              top-4
              start-4
              inline-flex
              max-w-[85%]
              items-center
              gap-2
              rounded-full
              bg-color-1
              px-3
              py-1.5
              text-white
              text-xs
              font-kufi
              shadow-lg
            "
          >
            <FaFireAlt className="shrink-0" />

            <span className="truncate">
              {categoryTitle || t("popular")}
            </span>
          </div>
        </div>

        {/* ================= Content ================= */}

        <div
          className="
            flex
            flex-1
            flex-col
            justify-between
            p-5
            md:p-7
          "
        >
          <div>
            {/* Title + Rating */}
            <div
              className="
                mb-3
                flex
                items-start
                justify-between
                gap-4
              "
            >
              <h3
                className="
                  h6
                  line-clamp-2
                  text-tcolor-4
                  dark:text-white
                  transition-colors
                  group-hover:text-color-1
                "
              >
                {title}
              </h3>

              {/* Rating */}
              <div
                className="
                  shrink-0
                  flex
                  items-center
                  gap-1.5
                  rounded-full
                  bg-color-1/10
                  px-3
                  py-1.5
                "
              >
                <FiStar
                  size={14}
                  className="
                    fill-yellow-500
                    text-yellow-500
                  "
                />

                <span
                  className="
                    text-sm
                    font-semibold
                    text-tcolor-4
                    dark:text-white
                  "
                >
                  {rating}
                </span>
              </div>
            </div>

            {/* Description */}
            <p
              className="
                body-2
                mb-4
                line-clamp-2
                text-n-5
                dark:text-n-3
              "
            >
              {description}
            </p>

            {/* Preparation Time */}
            <div
              className="
                flex
                items-center
                gap-2
                text-sm
                text-n-5
                dark:text-n-4
              "
            >
              <FiClock />

              <span
                className="
                  font-kufi
                  font-light
                "
              >
                {preparationTime}
              </span>
            </div>
          </div>

          {/* ================= Bottom ================= */}

          <div
            className="
              mt-6
              flex
              flex-col
              gap-4
              sm:flex-row
              sm:items-end
              sm:justify-between
            "
          >
            {/* Price */}
            <div className="min-w-0">
              {hasPrice ? (
                <div
                  className="
                    flex
                    flex-wrap
                    items-center
                    gap-x-3
                    gap-y-2
                  "
                >
                  {/* Main Price */}
                  <div
                    className="
                      flex
                      items-end
                      gap-2
                    "
                  >
                    <span
                      className="
                        text-2xl
                        md:text-3xl
                        font-bold
                        leading-none
                        text-color-1
                      "
                    >
                      {formattedPrice}
                    </span>

                    <span
                      className="
                        mb-0.5
                        font-kufi
                        text-sm
                        text-color-1
                      "
                    >
                      {t("pounds")}
                    </span>
                  </div>

                  {/* Tax Note */}
                  <span
                    className="
                      inline-flex
                      items-center
                      whitespace-nowrap
                      rounded-full
                      border
                      border-color-1/15
                      bg-color-1/5
                      px-2.5
                      py-1
                      font-kufi
                      text-sm
                      leading-5
                      text-n-5
                      dark:border-color-1/20
                      dark:bg-color-1/10
                      dark:text-n-3
                      md:text-sm
                    "
                  >
                    {t("priceExcludesTax")}
                  </span>
                </div>
              ) : (
                <span
                  className="
                    font-kufi
                    text-color-1
                  "
                >
                  {t("today price")}
                </span>
              )}
            </div>

            {/* Details Button */}
            <Link
              to={`/MealDetails/${meals?._id}`}
              onClick={handleScrollTop}
              className="
                inline-flex
                shrink-0
                items-center
                justify-center
                gap-2
                rounded-xl
                bg-color-1
                px-5
                py-3
                text-sm
                text-white
                font-kufi
                shadow-lg
                transition-all
                duration-300
                hover:-translate-y-1
                hover:bg-color-3
              "
            >
              <span>
                {t("More Details")}
              </span>

              <FiArrowRight
                className={
                  currentDir === "rtl"
                    ? "rotate-180"
                    : ""
                }
              />
            </Link>
          </div>
        </div>
      </motion.article>
    );
  }

  // =========================================================
  // GRID VIEW
  // =========================================================

  return (
    <motion.article
      whileHover={{ y: -8 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 24,
      }}
      className="
        group
        relative
        flex
        h-full
        flex-col
        overflow-hidden
        rounded-2xl
        md:rounded-3xl
        border
        border-color-1/10
        bg-white
        dark:bg-back-darktertiary
        shadow-lg
        transition-all
        duration-500
        hover:border-color-1/30
        hover:shadow-2xl
      "
    >
      {/* ================= Image ================= */}

      <div
        className={`
          relative
          shrink-0
          overflow-hidden
          ${
            compact
              ? "h-36 sm:h-48 md:h-64"
              : "h-60 md:h-64"
          }
        `}
      >
        <img
          src={`${media}${meals?.Image}`}
          alt={title}
          loading="lazy"
          className="
            w-full
            h-full
            object-cover
            transition-transform
            duration-700
            group-hover:scale-110
          "
        />

        {/* Overlay */}
        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/45
            via-transparent
            to-black/5
          "
        />

        {/* ================= Category ================= */}

        <div
          className={`
            absolute
            top-3
            start-3
            md:top-4
            md:start-4
            flex
            max-w-[85%]
            items-center
            gap-1.5
            rounded-full
            bg-color-1
            text-white
            font-kufi
            shadow-lg
            ${
              compact
                ? `
                  px-2
                  py-1
                  text-[9px]
                  md:px-3
                  md:py-1.5
                  md:text-xs
                `
                : `
                  px-3
                  py-1.5
                  text-xs
                `
            }
          `}
        >
          <FaFireAlt className="shrink-0" />

          <span className="truncate">
            {categoryTitle || t("popular")}
          </span>
        </div>

        {/* ================= Rating ================= */}

        <div
          className={`
            absolute
            bottom-3
            end-3
            flex
            items-center
            gap-1
            rounded-full
            bg-black/55
            backdrop-blur-md
            text-white
            ${
              compact
                ? "px-2 py-1 text-[10px]"
                : "px-3 py-1.5 text-xs"
            }
          `}
        >
          <FiStar
            className="
              fill-yellow-400
              text-yellow-400
            "
          />

          <span>
            {rating}
          </span>
        </div>
      </div>

      {/* ================= Content ================= */}

      <div
        className={`
          flex
          flex-1
          flex-col
          ${
            compact
              ? "p-3 md:p-5"
              : "p-5"
          }
        `}
      >
        {/* Title */}
        <h3
          className={`
            line-clamp-1
            font-kufi
            font-semibold
            text-tcolor-4
            dark:text-white
            transition-colors
            group-hover:text-color-1
            ${
              compact
                ? "text-sm leading-6 md:text-lg"
                : "text-lg"
            }
          `}
        >
          {title}
        </h3>

        {/* ================= Time ================= */}

        <div
          className={`
            mt-2
            items-center
            gap-1.5
            text-n-5
            dark:text-n-4
            ${
              compact
                ? "hidden md:flex"
                : "flex"
            }
          `}
        >
          <FiClock size={13} />

          <span
            className="
              font-kufi
              text-xs
            "
          >
            {preparationTime}
          </span>
        </div>

        {/* ================= Description ================= */}

        <p
          className={`
            mt-3
            line-clamp-2
            text-sm
            text-n-5
            dark:text-n-3
            ${
              compact
                ? "hidden md:block"
                : "block"
            }
          `}
        >
          {description}
        </p>

        {/* =================================================
            Price + Button
        ================================================= */}

        <div
          className={`
            mt-auto
            flex
            items-end
            justify-between
            gap-2
            ${
              compact
                ? "pt-3 md:pt-5"
                : "pt-5"
            }
          `}
        >
          {/* ================= Price ================= */}

          <div className="min-w-0 flex-1">
            {hasPrice ? (
              <div
                className="
                  flex
                  flex-col
                  items-start
                  gap-1.5
                "
              >
                {/* Main Price */}
                <div
                  className="
                    flex
                    items-end
                    gap-1
                  "
                >
                  <span
                    className={`
                      font-bold
                      leading-none
                      text-color-1
                      ${
                        compact
                          ? "text-lg md:text-2xl"
                          : "text-2xl"
                      }
                    `}
                  >
                    {formattedPrice}
                  </span>

                  <span
                    className={`
                      mb-0.5
                      font-kufi
                      text-color-1
                      ${
                        compact
                          ? "text-[9px] md:text-xs"
                          : "text-xs"
                      }
                    `}
                  >
                    {t("pounds")}
                  </span>
                </div>

                {/* Tax Note */}
                <span
                  className={`
                    inline-flex
                    max-w-full
                    items-center
                    whitespace-nowrap
                    rounded-full
                    border
                    border-color-1/15
                    bg-color-1/5
                    font-kufi
                    leading-5
                    text-n-5
                    dark:border-color-1/20
                    dark:bg-color-1/10
                    dark:text-n-3
                    ${
                      compact
                        ? `
                          px-1.5
                          py-0.5
                          text-[7px]
                          md:px-2
                          md:text-[9px]
                        `
                        : `
                          px-2
                          py-0.5
                          text-[9px]
                          md:text-[10px]
                        `
                    }
                  `}
                >
                  {t("priceExcludesTax")}
                </span>
              </div>
            ) : (
              <span
                className={`
                  font-kufi
                  text-color-1
                  ${
                    compact
                      ? "text-[10px] md:text-sm"
                      : "text-sm"
                  }
                `}
              >
                {t("today price")}
              </span>
            )}
          </div>

          {/* ================= Details Button ================= */}

          <Link
            to={`/MealDetails/${meals?._id}`}
            onClick={handleScrollTop}
            aria-label={t("More Details")}
            className={`
              shrink-0
              flex
              items-center
              justify-center
              rounded-xl
              bg-color-1
              text-white
              shadow-lg
              transition-all
              duration-300
              hover:-translate-y-0.5
              hover:bg-color-3
              ${
                compact
                  ? `
                    w-9
                    h-9
                    md:w-auto
                    md:h-11
                    md:px-4
                    md:gap-2
                  `
                  : `
                    h-11
                    px-4
                    gap-2
                  `
              }
            `}
          >
            <span
              className={`
                font-kufi
                text-xs
                ${
                  compact
                    ? "hidden md:inline"
                    : "inline"
                }
              `}
            >
              {t("More Details")}
            </span>

            <FiArrowRight
              className={`
                transition-transform
                ${
                  currentDir === "rtl"
                    ? "rotate-180"
                    : ""
                }
              `}
            />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

export default MealsCard;